// add this import near the top with the others
import StaffDev from './staff_dev';
import './ai-modal.css';
// add this import near the top with the others
import RouteAnalysis from "./route_analysis";
import SMSQueue from "./SMSQueue";
import PwaDev from "./pwa_dev";

import React, { useState, useMemo } from "react";
import Login from "./Login";
import QMap from "./QMap";
import StockFinancials from "./stock_financials";
import StockGraphs from "./stock_graphs";
import Mockup from "./Mockupp";
import StockExcess from "./stock_excess";
import ShelfPictorialView from "./shelfPictoriallView";
import Pickups from "./pickups";
import Settings from "./settings";
import Shelves from "./shelves";
import Updates from './updates';
import Stafflogin from './stafflogin';
import Sms from './sms';
import SmsProd from './smsprod';
import Sitedetails from './sitedetails';
import DriverRoute from "./driverroute";
import ApptsAnalysis from "./appts_analysis"; // <-- added
import Epos from "./epos"; // <-- added

import QuestionBox from "./QuestionBox";
import "./App.css";

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const App = () => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userData, setUserData] = useState(null);
  const [currentView, setCurrentView] = useState("mockup");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shelvesOpen, setShelvesOpen] = useState(false);
  const [answer, setAnswer] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [clarificationOptions, setClarificationOptions] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("");
  const [showStockMenu, setShowStockMenu] = useState(false);

  // Pagination & Sorting & Filtering
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [sortKey, setSortKey] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [question, setQuestion] = useState("");

  const handleShelvesClick = () => setShelvesOpen(true);
  const handleSettingsClick = () => setSettingsOpen(true);
  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
  };

  const handleAskQuestion = async (question) => {
    if (!question.trim()) return;
    setQuestion(question);
    setLoadingAnswer(true);
    setClarificationOptions(null);
    setAnswer("");
    setCurrentPage(1);
    const members = JSON.parse(localStorage.getItem("members") || "[]");
    try {
      const res = await fetch("https://api.pharmacegroup.co.uk/aiquery", {
        method: "POST",
        headers: { "Content-Type": "application/json",
  "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ question, members })
      });

      if (!res.ok) {
        console.error("Server returned an error status:", res.status);
        setAnswer(`Server error: ${res.status}`);
        setShowDialog(true);
        return;
      }

      const data = await res.json();
      if (data.needClarification) {
        setClarificationOptions({
          message: data.message,
          missingFields: data.missingFields
        });
        return;
      }

      setAnswer(data.answer || "No response received.");
      setShowDialog(true);

      await fetch("https://api.pharmacegroup.co.uk/savequestion", {
        method: "POST",
        headers: { "Content-Type": "application/json",
  "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          groupId: localStorage.getItem("userId"),
          question
        })
      });
    } catch (error) {
      console.error("Error during /aiquery fetch:", error);
      setAnswer("An error occurred while fetching data.");
      setShowDialog(true);
    } finally {
      setLoadingAnswer(false);
    }
  };

  const handleClarifiedQuery = async () => {
    if (!selectedPharmacy || !selectedMetric) return;
    const newQuestion = `What is the ${selectedMetric} at ${selectedPharmacy}?`;
    await handleAskQuestion(newQuestion);
  };


  const handleExportToExcel = () => {
    if (!Array.isArray(answer) || answer.length === 0) return;

    const exportData = answer.map(item => {
      const filtered = {};
      for (const [key, value] of Object.entries(item)) {
        if (["_id", "log"].includes(key)) continue;
        if (["price", "value", "totalPrice", "totalValue"].includes(key) && typeof value === "number") {
          filtered[key] = (value / 100).toFixed(2);
        } else {
          filtered[key] = value;
        }
      }
      return filtered;
    });

    import("xlsx").then((xlsx) => {
      const workbook = xlsx.utils.book_new();
      const worksheet = {};

      // Add top headers manually
      const groupName = localStorage.getItem("organisationName") || "Group";
      const now = new Date();
      const formatted = now.toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit"
      });
      const questionPrompt = question || "";

      const headerRows = [
        [groupName],
        [`Generated at: ${formatted}`],
        [`Prompt: ${questionPrompt}`],
        [] // blank line before table headers
      ];
      xlsx.utils.sheet_add_aoa(worksheet, headerRows, { origin: "A1" });

      // Insert column headers manually at row 5
      const headers = Object.keys(exportData[0] || {});
      xlsx.utils.sheet_add_aoa(worksheet, [headers], { origin: "A5" });

      // Insert data starting from row 6
      xlsx.utils.sheet_add_json(worksheet, exportData, {
        origin: "A6",
        skipHeader: true
      });

      // Auto column widths
      const allRows = [...headerRows, headers, ...exportData.map(obj => headers.map(k => obj[k] ?? ""))];
      worksheet["!cols"] = headers.map((_, colIdx) => {
        const maxLength = allRows.reduce((max, row) => {
          const val = row[colIdx];
          const len = val ? String(val).length : 0;
          return Math.max(max, len);
        }, 10);
        return { wch: maxLength + 2 };
      });

      xlsx.utils.book_append_sheet(workbook, worksheet, "AI_Results");
      xlsx.writeFile(workbook, "AI_Response.xlsx");
    });
  };

  const filteredAndSorted = useMemo(() => {
    if (!Array.isArray(answer)) return [];

    let result = [...answer];

    if (filterText) {
      result = result.filter(row =>
        Object.values(row).some(val =>
          String(val).toLowerCase().includes(filterText.toLowerCase())
        )
      );
    }

    if (sortKey) {
      result.sort((a, b) => {
        const valA = a[sortKey];
        const valB = b[sortKey];
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (typeof valA === "number" && typeof valB === "number") {
          return sortAsc ? valA - valB : valB - valA;
        }
        return sortAsc
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return result;
  }, [answer, filterText, sortKey, sortAsc]);

  const totalPages = Math.ceil(filteredAndSorted.length / pageSize);
  const paginatedAnswer = filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="app-container">
        {!token ? (
          <Login setToken={setToken} setUserData={setUserData} />
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "15px", marginTop: "20px", marginBottom: "10px", flexWrap: "nowrap" }}>
              <h2 className="group-title" style={{ margin: 0, whiteSpace: "nowrap" }}>
                {localStorage.getItem("organisationName") || "Group"} management tools
              </h2>
              <QuestionBox onAsk={handleAskQuestion} />
            </div>

            {clarificationOptions && (
              <div style={{ marginTop: 20 }}>
                <p>{clarificationOptions.message}</p>
                {clarificationOptions.missingFields.includes("pharmacy name") && (
                  <>
                    <label>Choose Pharmacy: </label>
                    <select value={selectedPharmacy} onChange={(e) => setSelectedPharmacy(e.target.value)}>
                      <option value="">--Select--</option>
                      <option value="Oxford Pharmacy">Oxford Pharmacy</option>
                      <option value="High Street Pharmacy">High Street Pharmacy</option>
                      <option value="City Centre Pharmacy">City Centre Pharmacy</option>
                    </select>
                  </>
                )}
                {clarificationOptions.missingFields.includes("metric") && (
                  <>
                    <label> Choose Metric: </label>
                    <select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)}>
                      <option value="">--Select--</option>
                      <option value="totalCost">Total Cost</option>
                      <option value="count">Count</option>
                      <option value="totalPackets">Total Packets</option>
                      <option value="averagePrice">Average Price</option>
                      <option value="expiringSoon">Expiring Soon</option>
                    </select>
                  </>
                )}
                <button
                  onClick={handleClarifiedQuery}
                  disabled={
                    (clarificationOptions.missingFields.includes("pharmacy name") && !selectedPharmacy) ||
                    (clarificationOptions.missingFields.includes("metric") && !selectedMetric)
                  }
                  style={{ marginLeft: "10px" }}
                >
                  Submit Clarified Query
                </button>
              </div>
            )}

            <div className="button-bar" style={{ display: "flex", justifyContent: "space-between", flexWrap: "nowrap", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "nowrap", position: "relative" }}>
                <button onClick={() => setCurrentView("mockup")}>Site Activity</button>
                <button onClick={() => setCurrentView("qmap")}>Map</button>
                <button onClick={() => setShowStockMenu(!showStockMenu)}>Stock ▼</button>
                {showStockMenu && (
                  <div style={{ position: "absolute", top: "35px", left: "160px", background: "#fff", border: "1px solid #ccc", padding: "5px", zIndex: 100 }}>
                    <button onClick={() => { setCurrentView("stock_financials"); setShowStockMenu(false); }}>Financials</button>
                    <button onClick={() => { setCurrentView("shelfPictorialView"); setShowStockMenu(false); }}>Pictorial</button>
                    <button onClick={() => { setCurrentView("stock_graphs"); setShowStockMenu(false); }}>Graphs</button>
                    <button onClick={() => { setCurrentView("stock_excess"); setShowStockMenu(false); }}>Excess</button>
                  </div>
                )}
                <button onClick={() => setCurrentView("stafflogin")}>Staff clocking in/out</button>
                <button onClick={() => setCurrentView("pickups")}>Collection times</button>
                <button onClick={() => setCurrentView("smsprod")}>SMS history</button>

                {(localStorage.getItem("organisationName") || "").trim() === "Pharmace all" && (
                  <>
                    <button onClick={() => setCurrentView("sms")}>SMS dev</button>
                    <button onClick={() => setCurrentView("staff_dev")}>STAFF dev</button>
                        <button onClick={() => setCurrentView("pwa_dev")}>PWA dev</button>
                    <button onClick={() => setCurrentView("sms_queue")}>SMS queue</button>
                    <button onClick={() => setCurrentView("sitedetails")}>Site details</button>
                  </>
                )}

                <button onClick={() => setCurrentView("route_analysis")}>Route analysis</button>
                <button onClick={() => setCurrentView("appts_analysis")}>Appointments analysis</button> {/* <-- added */}
                <button onClick={() => setCurrentView("epos")}>EPOS</button> {/* <-- added */}
                <button onClick={() => setCurrentView("updates")}>Updates and feedback</button>
              </div>
              <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
                <button onClick={handleShelvesClick}>Stock Shelves</button>
                <button onClick={handleSettingsClick}>Settings</button>
                <button onClick={handleLogout}>Log out</button>
              </div>
            </div>

            {currentView === "mockup" && <Mockup userData={userData} />}
            {currentView === "qmap" && <QMap userData={userData} />}
            {currentView === "stock_financials" && <StockFinancials userData={userData} />}
            {currentView === "shelfPictorialView" && <ShelfPictorialView userData={userData} />}
            {currentView === "stock_graphs" && <StockGraphs userData={userData} />}
            {currentView === "stock_excess" && <StockExcess userData={userData} />}
            {currentView === "pickups" && <Pickups userData={userData} />}
            {currentView === "stafflogin" && <Stafflogin userData={userData} />}
            {currentView === "smsprod" && <SmsProd userData={userData} />}
            {currentView === "sms" && <Sms userData={userData} />}
            {currentView === "sms_queue" && <SMSQueue userData={userData} />}
            {currentView === "staff_dev" && <StaffDev userData={userData} />} {/* <-- added */}
            {currentView === "pwa_dev" && <PwaDev userData={userData} />}
            {currentView === "route_analysis" && <RouteAnalysis userData={userData} />}

            {currentView === "appts_analysis" && <ApptsAnalysis userData={userData} />} {/* <-- added */}
            {currentView === "epos" && <Epos userData={userData} />} {/* <-- added */}

            {currentView === "sitedetails" && <Sitedetails userData={userData} />}
         

            {currentView === "updates" && <Updates userData={userData} />}
            <Shelves open={shelvesOpen} onClose={() => setShelvesOpen(false)} />
            <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />

            {showDialog && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                zIndex: 1000
              }}>
                <div style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "8px",
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  marginTop: "30px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h3 style={{ margin: 0 }}>AI Response - </h3>
                    <div>
                      <button onClick={handleExportToExcel} style={{ marginRight: "10px" }}>Export to Excel</button>
                      <button onClick={() => setShowDialog(false)}>Close</button>
                    </div>
                  </div>

                  {Array.isArray(answer) && (
                    <>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={filterText}
                        onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
                        style={{ marginBottom: "10px", padding: "5px", width: "100%" }}
                      />
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ borderCollapse: "collapse", width: "100%" }}>
                          <thead>
                            <tr>
                              {Object.keys(answer[0] || {}).filter(k => !["_id", "id", "log"].includes(k)).map((key) => (
                                <th key={key}
                                  onClick={() => {
                                    if (sortKey === key) {
                                      setSortAsc(!sortAsc);
                                    } else {
                                      setSortKey(key);
                                      setSortAsc(true);
                                    }
                                  }}
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "8px",
                                    backgroundColor: "#f2f2f2",
                                    textTransform: "capitalize",
                                    cursor: "pointer"
                                  }}>
                                  {key} {sortKey === key ? (sortAsc ? "↑" : "↓") : ""}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedAnswer.map((item, index) => (
                              <tr key={index}>
                                {Object.entries(item).filter(([k]) => !["_id", "id", "log"].includes(k)).map(([key, value]) => {
                                  let displayValue = value;
                                  if (["price", "value", "totalPrice", "totalValue"].includes(key) && typeof value === "number") {
                                    displayValue = `£${(value / 100).toFixed(2)}`;
                                  } else if (typeof value === "object") {
                                    displayValue = JSON.stringify(value);
                                  } else {
                                    displayValue = String(value);
                                  }

                                  const isTruncatable = ["vmppNm", "amppNm"].includes(key) && displayValue.length > 25;
                                  const cellText = isTruncatable ? displayValue.slice(0, 25) + "…" : displayValue;

                                  return (
                                    <td
                                      key={key}
                                      style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        maxWidth: "200px",
                                        overflowWrap: "break-word"
                                      }}
                                      title={isTruncatable ? displayValue : ""}
                                    >
                                      {cellText}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: "10px", textAlign: "center" }}>
                        <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>Prev</button>
                        <span style={{ margin: "0 10px" }}>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
                      </div>
                    </>
                  )}

                  {!Array.isArray(answer) && (
                    <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {loadingAnswer ? "Searching..." : answer}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default App;
